import { useMemo } from 'react';
import type { Project } from '../../types/crm';
import { monthShortLabel } from '../../lib/dashboardMonthlySales';
import { buildClosedProjectsMonthBars } from '../../utils/projectHelpers';

type ProjectClosedBarChartProps = {
  projects: Project[];
  year: number;
};

export function ProjectClosedBarChart({ projects, year }: ProjectClosedBarChartProps) {
  const bars = useMemo(
    () => buildClosedProjectsMonthBars(projects, year),
    [projects, year],
  );
  const total = useMemo(() => bars.reduce((s, b) => s + b.count, 0), [bars]);
  const max = useMemo(() => Math.max(1, ...bars.map((b) => b.count)), [bars]);

  return (
    <div className="dashboard-closed-bars">
      <p className="dashboard-closed-bars__total">
        {total} closed in {year}
      </p>

      {total === 0 ? (
        <p className="dashboard-closed-bars__empty">
          No projects closed in {year} (by completed date).
        </p>
      ) : (
        <div
          className="dashboard-closed-bars__chart"
          role="img"
          aria-label={`Closed projects by month in ${year}`}
        >
          {bars.map((bar) => {
            const pct = bar.count === 0 ? 0 : Math.max(8, Math.round((bar.count / max) * 100));
            return (
              <div key={bar.month} className="dashboard-closed-bars__col">
                <span className="dashboard-closed-bars__value" title={`${bar.count} closed`}>
                  {bar.count > 0 ? bar.count : ''}
                </span>
                <div className="dashboard-closed-bars__track" aria-hidden>
                  <span
                    className={`dashboard-closed-bars__bar${bar.count === 0 ? ' is-empty' : ''}`}
                    style={{ height: `${pct}%` }}
                  />
                </div>
                <span className="dashboard-closed-bars__label">{monthShortLabel(bar.month)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
